'use client';

import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import {
  CheckIcon,
  EyeIcon,
  PencilIcon,
  XMarkIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/solid';
import {
  Anchor, Button, LinkStyled, TailwindSpinner, Tooltip,
} from '@components/common';
import { cn } from '@utils/cn';
import { useCompanyJobActions } from '@components/templates/my-jobs/hooks/useCompanyJobActions';
import { SuspendConfirmationDialog } from '@components/templates/my-jobs/SuspendConfirmationDialog';
import { useExtendedSearchParams } from '@hooks/next-routing-wrappers';

interface MyJobCardActionsProps {
  job: ApiSchema<'MyJobCard'>;
  revalidate?: (jobSlug: string, oldStatus: string, newStatus: string) => void;
  beforeModalOpen?: () => void;
}

const roundButtonClasses = 'flex items-center justify-center rounded-full border border-primary/20 p-3 text-primary transition-colors duration-200 hover:border-primary hover:bg-primary hover:text-white';
const mainButtonClasses = {
  base: 'flex grow items-center justify-center gap-2 rounded-full px-6 py-2.5 text-sm transition-colors duration-200 hover:text-white font-bold disabled:cursor-not-allowed',
  activate: 'border border-secondary/10 bg-secondary/10 text-secondary hover:bg-secondary hover:border-secondary disabled:border-secondary/10 disabled:bg-secondary/10 disabled:text-secondary',
  suspend: 'border border-error/10 bg-error/10 text-error hover:bg-error hover:border-error disabled:border-error/10 disabled:bg-error/10 disabled:text-error',
};

export const Actions = (props: MyJobCardActionsProps) => {
  const { job, revalidate, beforeModalOpen } = props;
  const { t } = useTranslation('common');

  const revalidateJob = useCallback((newStatus: string) => {
    revalidate?.(job.slug!, job.state!, newStatus);
  }, [revalidate, job]);

  const {
    activateJob,
    moveToPassive,
    moveToArchived,
    handleSuspensionConfirmation,
    handleSuspensionCancellation,
    isActivating,
    isMovingToPassive,
    isMovingToArchived,
    isSuspensionConfirmationOpen,
    suspensionType,
  } = useCompanyJobActions({
    jobSlug: job.slug!,
    eligibleForInstantActivation: job.eligibleForInstantActivation,
    currentJobState: job.state,
    onSuspension: () => revalidateJob('passive'),
    onActivation: () => revalidateJob('active'),
    onArchiving: () => revalidateJob('archived'),
  });

  const { searchParams } = useExtendedSearchParams();

  // Open job detail modal
  // *****************************************
  const handleJobOpening = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Check if Ctrl (Windows/Linux) or Meta (Mac) key is pressed
    if (e.ctrlKey || e.metaKey) {
      // Allow the default behavior to open in a new tab
      return;
    }

    // Prevent default action for regular clicks
    e.preventDefault();

    // Build the params object from existing searchParams
    const params = new URLSearchParams(searchParams?.toString());

    // Set the selectedJobSlug parameter
    params.set('selectedJobSlug', job.slug as string);

    // Construct the new URL with proper query parameters
    const newUrl = `/my-job/${job.slug}?${params.toString()}`;

    // Update the history with the new URL
    window.history.replaceState(null, '', newUrl);
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Tooltip
          tooltipText={t('my-jobs.card.actions.edit')}
          position="top"
          colorScheme="black"
          floating
          useFloatingPortal
        >
          <LinkStyled
            styling="none"
            className={roundButtonClasses}
            aria-label={t('my-jobs.card.actions.edit')}
            href={`/job-add/edit/${job.slug}`}
          >
            <PencilIcon className="size-4" />
          </LinkStyled>
        </Tooltip>
        <Tooltip
          tooltipText={t('my-jobs.card.actions.view')}
          position="top"
          colorScheme="black"
          floating
          useFloatingPortal
        >
          <Anchor
            styling="none"
            className={roundButtonClasses}
            aria-label={t('my-jobs.card.actions.view')}
            href={`/my-job/${job.slug}`}
            onMouseDown={beforeModalOpen}
            onClick={handleJobOpening}
          >
            <EyeIcon className="size-4" />
          </Anchor>
        </Tooltip>
        {job.state !== 'archived' && (
          <Tooltip
            tooltipText={t('my-jobs.card.actions.archive')}
            position="top"
            colorScheme="black"
            floating
            useFloatingPortal
          >
            <Button
              styling="none"
              className={cn(roundButtonClasses, mainButtonClasses.suspend)}
              aria-label={t('my-jobs.card.actions.suspend')}
              onClick={() => {
                if (isMovingToArchived) return;
                void moveToArchived();
              }}
              disabled={isMovingToArchived}
            >
              {
                isMovingToArchived
                  ? <TailwindSpinner className="size-4" />
                  : <ArchiveBoxIcon className="size-4" />
              }
            </Button>
          </Tooltip>
        )}

        {job.state === 'active' ? (
          <Button
            styling="none"
            className={cn(mainButtonClasses.base, mainButtonClasses.suspend)}
            aria-label={t('my-jobs.card.actions.suspend')}
            onClick={() => { void moveToPassive(); }}
            isLoading={isMovingToPassive}
            disabled={isMovingToPassive}
            spinnerClass="size-4"
          >
            <XMarkIcon className="size-4" />
            {t('my-jobs.card.actions.suspend')}
          </Button>
        ) : (
          <Button
            styling="none"
            className={cn(mainButtonClasses.base, mainButtonClasses.activate)}
            aria-label={t('my-jobs.card.actions.activate')}
            onClick={() => {
              if (isActivating) return;
              void activateJob();
            }}
            isLoading={isActivating}
            disabled={isActivating}
            spinnerClass="size-4"
          >
            {!isActivating && <CheckIcon className="size-4" /> }
            {t('my-jobs.card.actions.activate')}
          </Button>
        )}
      </div>
      <SuspendConfirmationDialog
        open={isSuspensionConfirmationOpen}
        suspensionType={suspensionType}
        onConfirm={() => {
          if (isMovingToPassive || isMovingToArchived) return;
          void handleSuspensionConfirmation();
        }}
        onCancel={handleSuspensionCancellation}
        isConfirmLoading={isMovingToPassive || isMovingToArchived}
      />
    </>
  );
};
